import React, { useState, FormEvent, useEffect, useMemo } from 'react'
import { connect, useDispatch } from 'react-redux'
import { Stack, Paper, Container } from '@mui/material'
import ProgressButton from '../../Utils/ProgressButton'
import { entityTextField, entitySelectField } from '../../Utils/EntityField'
import { AppState } from '../../../reducers/index'
import { fetchProfile, updateProfile } from '../../../actions/profile'
import { ProfileState } from '../../../reducers/profile'
import { Profile } from '../../../models/Profile'
import { literalStrings } from '../../../utils/StringUtils';
import { useTranslation } from 'react-i18next'
import { ISTableTitle } from '../../Utils/ISTable'
import { personTypeToDescription, visiblePersonTypes } from '.'

interface Props {
  profile: ProfileState
}

const empty: Profile = {
  firstName: '',
  lastName: '',
  phoneNumber: '',
  documentNumber: '',
  documentType: '',
  province: '',
  address: '',
  zipCode: '',
  locality: '',
  personType: '',
  byPersonTypeId: 0,
  id: ''
}

const PersonalInformation: React.FC<Props> = ({ profile }) => {
  const [changed, setChanged] = useState<boolean>(false)
  const [personalInformation, setPersonalInformation] = useState<Profile>(profile.profile || empty)
  const { t } = useTranslation('person')

  const change = (newPersonalInformation: Profile) => {
    setPersonalInformation(newPersonalInformation)
    setChanged(true)
  }

  const dispatch = useDispatch()
  const changeProfile = (event: FormEvent) => {
    event.preventDefault()
    dispatch(updateProfile(personalInformation))
    setChanged(false)
  }

  useEffect(() => {
    dispatch(fetchProfile)
  }, [dispatch])

  useEffect(() => {
    if (profile.profile)
      setPersonalInformation(profile.profile)
  }, [profile.profile])

  const PersonalInformationField = useMemo(() => entityTextField(change), [])

  const PersonalInformationSelectField = useMemo(() => entitySelectField(change),[])

  const translatePersonTypeDescription = ( value: string ): string => t(personTypeToDescription(value))

  return (
    <Container>
      <Paper style={{borderRadius: "10px", margin: "10px"}}>
      <ISTableTitle title={t('myInfo')}/>
        <Paper style={{padding: "10px"}}>
        <form onSubmit={changeProfile}>
          {[
            literalStrings(t("firstName"), "firstName"),
            literalStrings(t("lastName"), "lastName"),
            literalStrings(t("phoneNumber"), "phoneNumber"),
            literalStrings(t("documentNumber"), "documentNumber"),
            literalStrings(t("address"), "address"),
            literalStrings(t("province"), "province"),
          ].map(([label, attr], i) =>
            <PersonalInformationField
              key={i}
              variant="outlined"
              label={label}
              entity={personalInformation}
              attr={attr}
              size="small"
              required
              disabled
            />)
          }
          <Stack display="flex" flexDirection="row" flexGrow={1} >
          <PersonalInformationField
              key="zipCode"
              variant="outlined"
              label={t("zipCode")}
              entity={personalInformation}
              attr="zipCode"
              size="small"
              style={{maxWidth: "150px", marginRight:"10px"}}
              required
              disabled
            />
          <PersonalInformationField
              key="locality"
              variant="outlined"
              label={t("locality")}
              entity={personalInformation}
              attr="locality"
              size="small"
              required
              disabled
            />
          </Stack>
          <PersonalInformationField
              key="province"
              variant="outlined"
              label={t("province")}
              entity={personalInformation}
              attr="province"
              size="small"
              required
              disabled
            />
          <Stack display="flex" flexDirection="row" flexGrow={1} >
          <PersonalInformationSelectField
              key="personType"
              //variant="outlined"
              label={t("personType")}
              entity={personalInformation}
              attr="personType"
              menuItems={visiblePersonTypes}
              textAdapter={translatePersonTypeDescription}
              style={{width: "200px", marginRight:"10px"}}
              required
              disabled
              //value={personTypeToDescription}
            />
          <PersonalInformationField
              key="byPersonTypeId"
              variant="outlined"
              label={t("byPersonTypeId")}
              entity={personalInformation}
              attr="byPersonTypeId"
              size="medium"
              required
              disabled
            />
          </Stack>
          <ProgressButton state={profile} disabled={!changed}>
            {t('save')}
          </ProgressButton>
        </form>
      </Paper>
      </Paper>
    </Container>

  )
}

const mapStateToProps = (state: AppState) => ({
  profile: state.profile
})

export default connect<Props, {}, {}, AppState>(mapStateToProps, {})(PersonalInformation)
